import { useSecureFields } from '@gr4vy/secure-fields-react'
import React, { useState, useImperativeHandle } from 'react'
import { useDispatch } from 'react-redux'

import { addFlashMessage } from '~/actions'
import { useGr4vyCheckoutSession } from '~/contexts/gr4vy-checkout-session'
import { i18n } from '~/i18n'

import DoubleBounceSpinner from '../double-bounce-spinner'
import Gr4vyCreditCardForm from './gr4vy-credit-card-form.component'
import { useCreateCreditCard } from './gr4vy-credit-card-form.utils'
import LoadingModal from '../loading-modal'

type Props = {
  saveAsCardOnFile: boolean
  setAsCartCreditCard: boolean
  onSaveSuccess: () => void
}

export default function Gr4vyCreditCardFormContainer({
  saveAsCardOnFile,
  setAsCartCreditCard,
  onSaveSuccess,
}: Props) {
  const { formRef, ready, profileId, checkoutSessionId } =
    useGr4vyCheckoutSession()
  const { secureFields } = useSecureFields()
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [nameOnCard, setNameOnCard] = useState('')
  const [nameOnCardError, setNameOnCardError] = useState(false)
  const [inputValidStates, setInputValidStates] = useState<{
    expiryDate: boolean
    cardNumber: boolean
    securityCode: boolean
  }>({
    expiryDate: false,
    cardNumber: false,
    securityCode: false,
  })
  const [hasBlurredStates, setHasBlurredStates] = useState<{
    expiryDate: boolean
    cardNumber: boolean
    securityCode: boolean
  }>({
    expiryDate: false,
    cardNumber: false,
    securityCode: false,
  })
  const { trigger: createPaymentMethod } = useCreateCreditCard(profileId)

  useImperativeHandle(
    formRef,
    () => ({
      onFormChange(data) {
        if (data) {
          setInputValidStates({
            expiryDate: data.fields.expiryDate.valid,
            cardNumber: data.fields.number.valid,
            securityCode: data.fields.securityCode.valid,
          })
        }
      },
      onSubmit() {
        setHasBlurredStates({
          expiryDate: true,
          cardNumber: true,
          securityCode: true,
        })
        setNameOnCardError(nameOnCard === '')
        if (!inputValidStates.expiryDate) return
        if (!inputValidStates.cardNumber) return
        if (!inputValidStates.securityCode) return
        if (nameOnCard === '') return

        setIsSubmitting(true)
        secureFields.submit()
      },
      async onCardVaultSuccess() {
        try {
          await createPaymentMethod({
            checkoutSessionId,
            nameOnCard,
            saveAsCardOnFile,
            setAsCartCreditCard,
          })

          await onSaveSuccess()
        } catch {
          dispatch(
            addFlashMessage(
              'error',
              i18n.t('components.flash_messages.default_error'),
            ),
          )
        } finally {
          setIsSubmitting(false)
        }
      },
    }),
    [nameOnCard, inputValidStates],
  )

  if (!ready) return <DoubleBounceSpinner />

  return (
    <>
      {isSubmitting && <LoadingModal />}
      <Gr4vyCreditCardForm
        nameOnCardError={nameOnCardError}
        cardNumberError={
          !inputValidStates.cardNumber && hasBlurredStates.cardNumber
        }
        expiryDateError={
          !inputValidStates.expiryDate && hasBlurredStates.expiryDate
        }
        securityCodeError={
          !inputValidStates.securityCode && hasBlurredStates.securityCode
        }
        onCardNumberBlur={() =>
          setHasBlurredStates((hasBlurredStates) => ({
            ...hasBlurredStates,
            cardNumber: true,
          }))
        }
        onExpiryDateBlur={() =>
          setHasBlurredStates((hasBlurredStates) => ({
            ...hasBlurredStates,
            expiryDate: true,
          }))
        }
        onSecurityCodeBlur={() =>
          setHasBlurredStates((hasBlurredStates) => ({
            ...hasBlurredStates,
            securityCode: true,
          }))
        }
        nameOnCard={nameOnCard}
        onNameOnCardChange={(value) => {
          setNameOnCard(value)
          setNameOnCardError(value === '')
        }}
      />
    </>
  )
}
